import { useMemo, useState } from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/utils/utils";
import { Check, ChevronsUpDown } from "lucide-react";

export interface AutocompleteOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

interface AutocompleteSelectProps {
  options: AutocompleteOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  displayValue?: (option: AutocompleteOption | undefined) => React.ReactNode;
  searchValue?: (option: AutocompleteOption) => string;
  hasError?: boolean;
  manual?: boolean; // Allow manual input even if not in options
  filterMode?: "fuzzy" | "includes" | "startsWith";
  maxWidth?: string;
}

const AutocompleteSelect = ({
  options,
  value,
  onValueChange,
  placeholder = "Seleziona un'opzione...",
  searchPlaceholder = "Cerca...",
  emptyMessage = "Nessun risultato trovato.",
  disabled = false,
  className,
  triggerClassName,
  displayValue,
  searchValue,
  hasError = false,
  manual = false,
  filterMode = "fuzzy",
  maxWidth
}: AutocompleteSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const selectedOption = options.find(option => option.value === value);

  const handleSelect = (selectedValue: string) => {
    onValueChange(selectedValue);
    setOpen(false);
  };

  const handleManualInput = () => {
    if (manual && searchInput.trim()) {
      onValueChange(searchInput.trim());
      setSearchInput("");
      setOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (manual && e.key === 'Enter' && searchInput.trim()) {
      e.preventDefault();
      handleManualInput();
    }
  };

  const defaultDisplayValue = (option: AutocompleteOption | undefined) => {
    if (!option) {
      // If manual mode and we have a value that's not in options, show it
      if (manual && value && !options.find(opt => opt.value === value)) {
        return <span className="truncate">{value}</span>;
      }
  return <span className="truncate text-muted-foreground">{placeholder}</span>;
    }
    
    const label = option.description
      ? `${option.label} - ${option.description}`
      : option.label;

    return (
      <div className="flex min-w-0 items-center gap-2">
        {option.icon && <span className="text-lg shrink-0">{option.icon}</span>}
        <span className="truncate">{label}</span>
      </div>
    );
  };

  const defaultSearchValue = (option: AutocompleteOption) => {
    return `${option.value} ${option.label} ${option.description || ''}`;
  };

  const commandFilter = useMemo(() => {
    if (!filterMode || filterMode === "fuzzy") {
      return undefined;
    }

    return (value: string, search: string) => {
      const normalizedSearch = search.trim().toLowerCase();
      if (!normalizedSearch) {
        return 1;
      }

      const normalizedValue = value.toLowerCase();

      if (filterMode === "startsWith") {
        return normalizedValue.startsWith(normalizedSearch) ? 1 : 0;
      }

      return normalizedValue.includes(normalizedSearch) ? 1 : 0;
    };
  }, [filterMode]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full font-normal rounded-md border bg-background px-3 py-2 hover:bg-gray-100 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50 disabled:border-input md:text-sm justify-between",
            hasError ? "border-yellow-400" : "border-input",
            triggerClassName
          )}
          style={maxWidth ? { maxWidth } : undefined}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-left">
            {displayValue ? (
              <span className="flex min-w-0 flex-1 overflow-hidden">
                {displayValue(selectedOption)}
              </span>
            ) : (
              defaultDisplayValue(selectedOption)
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-full p-0", className)} align="start" style={{ width: 'var(--radix-popover-trigger-width)' }}>
  <Command filter={commandFilter}>
          <CommandInput 
            placeholder={searchPlaceholder} 
            value={searchInput}
            onValueChange={setSearchInput}
            onKeyDown={handleKeyDown}
          />
          <CommandList>
            <CommandEmpty>
              {manual && searchInput.trim() ? (
                <div className="p-2">
                  <div className="text-sm text-gray-600 mb-2">Nessun risultato trovato per "{searchInput}"</div>
                  <button
                    type="button"
                    onClick={handleManualInput}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Usa "{searchInput}" (premi Invio)
                  </button>
                </div>
              ) : (
                emptyMessage
              )}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={searchValue ? searchValue(option) : defaultSearchValue(option)}
                  onSelect={() => handleSelect(option.value)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex items-center gap-2">
                    {option.icon && <span className="text-lg">{option.icon}</span>}
                    <span>{option.label}</span>
                    {option.description && (
                      <>
                        <span className="text-gray-500">-</span>
                        <span className="text-gray-500">{option.description}</span>
                      </>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AutocompleteSelect;
